import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
// components
import FormMessage from "../../components/FormMessage";
import SectionLoader from "../../components/SectionLoader";
import NoData from "../../components/NoData";
import MarketplaceTopBar from "./MarketplaceTopBar";
import MarketplaceItem from "./MarketplaceItem";
import MarketplaceNoOffers from "./MarketplaceNoOffers";
// hooks
import { useDidMountEffect } from "../../common/hooks/useDidMountEffect";
// helpers
// @ts-ignore
import { getSearchedOffers, sortCategoriesByPriority, sortGroupedOffersByCategory } from "@gl-shared/shared/helpers";
import { appSection, initialSelectedCategory } from "../../common/utils/panel-helpers/helpers";
// actions
import { marketplaceActionCreators } from "../../common/store/modules/marketplaceModule";

const Marketplace = ({
  offers,
  categories,
  selectedCategory,
  requestError,
  sectionLoadingNames,
  formData,
  selectedHotels,
  isLoadingMarketplace,
  updateMarketplaceOffersAction,
  changeSelectedCategoryAction,
  resetMarketplaceDataAction,
  hotelData
}) => {
  const outletContext = useOutletContext();

  const [groupedOffers, setGroupedOffers] = useState({});
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // load marketplace data initially (after we get hotel-info data) and on every hotel change
    if (hotelData?.hotelUserId) {
      outletContext?.loadMarketplaceData(selectedHotels[0]?.id);
    }
  }, [selectedHotels[0]?.id, hotelData]);

  // For Hotel Group Manager: After changing Hotel Reset searchQuery & set to "All" category
  useDidMountEffect(() => {
    onChangeCategory(initialSelectedCategory);
    setSearchQuery("");
  }, [selectedHotels[0]?.id]);

  useEffect(() => {
    return () => {
      resetMarketplaceDataAction();
    };
  }, []);

  useEffect(() => {
    if (searchQuery?.length > 0) {
      const filteredOffers = getSearchedOffers(offers, searchQuery);
      setGroupedOffers(sortGroupedOffersByCategory(filteredOffers, categories));
    } else {
      setGroupedOffers(sortGroupedOffersByCategory(offers, categories));
    }
  }, [offers, categories]);

  const onChangeCategory = category => changeSelectedCategoryAction(category);

  const handleSearchOffer = query => {
    setSearchQuery(query);
    const filteredOffers = getSearchedOffers(offers, query);
    if (query?.length > 0) {
      setIsSearchActive(true);
    } else {
      setIsSearchActive(false);
    }
    setGroupedOffers(sortGroupedOffersByCategory(filteredOffers, categories));
  };

  // Update channels on Marketplace
  const onUpdateOffer = async (offer, field, value) => await updateMarketplaceOffersAction(offer, field, value);

  const renderItems = obj => {
    const filteredOffers =
      selectedCategory?.short_name !== "all" ? obj.filter(([key, value]) => key === selectedCategory?.short_name) : obj;
    return filteredOffers?.length > 0 ? (
      filteredOffers.map(([key, value]) => {
        return (
          <div key={key}>
            <div className="marketplace__item-title">
              {categories.find(category => category.short_name === key)?.display_name || "-"}
            </div>
            <div className="marketplace__content-container">
              {value?.length > 0 &&
                value?.map(marketplace => (
                  <MarketplaceItem
                    key={marketplace?.event_code}
                    marketplace={marketplace}
                    onUpdateOffer={onUpdateOffer}
                  />
                ))}
            </div>
          </div>
        );
      })
    ) : (
      <MarketplaceNoOffers isSearchActive={isSearchActive} />
    );
  };

  return (
    <div className="marketplace component">
      {sectionLoadingNames === null || sectionLoadingNames?.includes(appSection?.marketplace) || isLoadingMarketplace ? (
        <SectionLoader loadingLabel="LOADING MARKETPLACE" />
      ) : (
        <>
          {requestError && <NoData message="RequestError" />}
          {formData?.form === "HotelSelectionNotice" && (
            <FormMessage form={formData?.form} hotelName={formData?.hotelName} />
          )}
          <MarketplaceTopBar
            categories={sortCategoriesByPriority(categories)}
            selectedCategory={selectedCategory}
            onCategory={onChangeCategory}
            onSearchInputChange={handleSearchOffer}
          />
          {groupedOffers.length > 0 ? (
            renderItems(groupedOffers)
          ) : (
            <MarketplaceNoOffers isSearchActive={isSearchActive} />
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  requestError: state.global.requestError,
  offers: state.marketplace.marketplaceOffers,
  categories: state.marketplace.marketplaceCategories,
  selectedCategory: state.marketplace.selectedMarketplaceCategory,
  isLoadingMarketplace: state.marketplace.isLoadingMarketplace,
  formData: state.global.formData,
  selectedHotels: state.global.selectedHotels,
  sectionLoadingNames: state.global.sectionLoadingNames,
  hotelData: state.home.hotelData
});

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      updateMarketplaceOffersAction: marketplaceActionCreators.updateMarketplaceOffersAction,
      changeSelectedCategoryAction: marketplaceActionCreators.changeSelectedCategoryAction,
      resetMarketplaceDataAction: marketplaceActionCreators.resetMarketplaceDataAction
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Marketplace);
